import React, { useState } from 'react';
import { X, ArrowRight, Sparkles } from 'lucide-react';

// ── ISO week key helper ──────────────────────────────────────────────────────
// Returns 'YYYY-WNN' for the current date (ISO-8601 week).
export function getWeekKey(date = new Date()) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  // Thursday in current week determines the year
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, '0')}`;
}

// ── Mood options ─────────────────────────────────────────────────────────────
export const MOOD_OPTIONS = [
  {
    id: 'great',
    emoji: '🤩',
    label: 'Great',
    score: 5,
    gradient: 'from-emerald-900/60 to-emerald-800/30',
    border: 'border-emerald-600/50',
    activeBg: 'bg-emerald-700/40',
    dot: '#10b981',
  },
  {
    id: 'good',
    emoji: '😊',
    label: 'Good',
    score: 4,
    gradient: 'from-blue-900/60 to-blue-800/30',
    border: 'border-blue-600/50',
    activeBg: 'bg-blue-700/40',
    dot: '#3b82f6',
  },
  {
    id: 'okay',
    emoji: '😐',
    label: 'Okay',
    score: 3,
    gradient: 'from-slate-700/60 to-slate-700/30',
    border: 'border-slate-500/50',
    activeBg: 'bg-slate-600/40',
    dot: '#94a3b8',
  },
  {
    id: 'stressed',
    emoji: '😟',
    label: 'Stressed',
    score: 2,
    gradient: 'from-amber-900/60 to-amber-800/30',
    border: 'border-amber-600/50',
    activeBg: 'bg-amber-700/40',
    dot: '#f59e0b',
  },
  {
    id: 'overwhelmed',
    emoji: '😰',
    label: 'Overwhelmed',
    score: 1,
    gradient: 'from-rose-900/60 to-rose-800/30',
    border: 'border-rose-600/50',
    activeBg: 'bg-rose-700/40',
    dot: '#ef4444',
  },
];

const fmt = (n) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);

// ── Response content factory ─────────────────────────────────────────────────
function buildResponse(mood, budgetData, goalsData, transactions = []) {
  const categories  = budgetData?.categories  || [];
  const income      = budgetData?.income      || 0;
  const bills       = budgetData?.bills       || [];
  const goals       = goalsData              || [];

  // Under-budget categories this month
  const underBudget = categories.filter(c => (c.spent || 0) < c.budgeted && c.budgeted > 0);

  // Upcoming unpaid bills in 7 days
  const now     = new Date();
  const in7     = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
  const dueSoon = bills.filter(b => {
    const due = new Date(now.getFullYear(), now.getMonth(), b.dueDay || 1);
    return !b.paidMonths?.[`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`]
      && due >= now && due <= in7;
  });

  // Savings vs income
  const totalSpent     = categories.reduce((s, c) => s + (c.spent || 0), 0);
  const budgeted       = categories.reduce((s, c) => s + (c.budgeted || 0), 0);
  const remaining      = Math.max(0, budgeted - totalSpent);
  const savingsTotal   = transactions
    .filter(t => t.kind === 'expense' && (t.type === 'Savings' || t.category === 'Savings' || t.category === 'Investments'))
    .reduce((s, t) => s + t.amount, 0);

  // Closest goal
  const closestGoal = [...goals].sort((a, b) => {
    const pa = (a.current || 0) / (a.target || 1);
    const pb = (b.current || 0) / (b.target || 1);
    return pb - pa;
  })[0];

  if (mood === 'great' || mood === 'good') {
    const positives = [];
    if (underBudget.length > 0) {
      positives.push(`You stayed under budget in ${underBudget.slice(0, 3).map(c => c.name).join(', ')}.`);
    }
    if (savingsTotal > 0) {
      positives.push(`You've put ${fmt(savingsTotal)} toward savings this month. That's real progress.`);
    }
    if (closestGoal) {
      const pct = Math.round(((closestGoal.current || 0) / (closestGoal.target || 1)) * 100);
      positives.push(`${closestGoal.icon || '🎯'} ${closestGoal.name} is ${pct}% funded. You're getting there.`);
    }
    if (remaining > 0 && budgeted > 0) {
      positives.push(`You still have ${fmt(remaining)} left in your budget this month.`);
    }
    if (positives.length === 0) {
      positives.push("You showed up and checked in — that alone puts you ahead of most people.");
    }
    return {
      heading: "That's wonderful! Your hard work is paying off.",
      body: "Here's what went well last week:",
      points: positives,
      color: mood === 'great' ? 'emerald' : 'blue',
    };
  }

  if (mood === 'okay') {
    const points = [];
    if (dueSoon.length > 0) {
      points.push(`${dueSoon.length} bill${dueSoon.length !== 1 ? 's' : ''} coming up in the next 7 days — ${dueSoon.map(b => b.name).join(', ')}.`);
    }
    if (remaining > 0 && budgeted > 0) {
      points.push(`You have ${fmt(remaining)} remaining in your budget. Use it intentionally.`);
    }
    if (income > 0) {
      points.push(`Tip: even saving 1% more this month adds up to ${fmt(income * 0.01)} — small steps compound.`);
    }
    if (points.length === 0) {
      points.push("Start by logging this week's biggest expense. Clarity always helps.");
    }
    return {
      heading: "That's totally normal. Let's look at the week ahead and make a plan together.",
      body: "A few things to keep in mind:",
      points,
      color: 'slate',
    };
  }

  // stressed / overwhelmed
  const overBudget  = categories.filter(c => (c.spent || 0) > c.budgeted && c.budgeted > 0);
  let suggestion;
  if (overBudget.length > 0) {
    const worst = overBudget.sort((a, b) => (b.spent - b.budgeted) - (a.spent - a.budgeted))[0];
    suggestion = `Focus on just one thing: ${worst.name}. You went ${fmt((worst.spent || 0) - worst.budgeted)} over. See if you can trim one expense there this week.`;
  } else if (dueSoon.length > 0) {
    suggestion = `Pay ${dueSoon[0].name} (${fmt(dueSoon[0].amount)}) first — clearing a bill removes one mental load immediately.`;
  } else if (income === 0) {
    suggestion = "Set your monthly income in the Budget tab. One number, two minutes — and suddenly everything feels more manageable.";
  } else {
    suggestion = `You're already tracking. That means you have more awareness than most. This week, just focus on one transaction per day — nothing more.`;
  }
  return {
    heading: "We're here for you.",
    body: "Remember — you're already doing better than most people just by tracking. Let's take it one step at a time.",
    points: [suggestion],
    color: 'amber',
  };
}

// ── Color maps ────────────────────────────────────────────────────────────────
const COLOR_MAP = {
  emerald: { bg: 'bg-emerald-950/50', border: 'border-emerald-700/40', heading: 'text-emerald-300', bullet: 'bg-emerald-500', body: 'text-emerald-200/80' },
  blue:    { bg: 'bg-blue-950/50',    border: 'border-blue-700/40',    heading: 'text-blue-300',    bullet: 'bg-blue-500',    body: 'text-blue-200/80'    },
  slate:   { bg: 'bg-slate-800/60',   border: 'border-slate-600/40',   heading: 'text-slate-200',   bullet: 'bg-slate-400',   body: 'text-slate-300/80'   },
  amber:   { bg: 'bg-amber-950/50',   border: 'border-amber-700/40',   heading: 'text-amber-300',   bullet: 'bg-amber-500',   body: 'text-amber-200/80'   },
};

// ── Main modal ────────────────────────────────────────────────────────────────
export default function MoodCheckinModal({ onClose, onComplete, budgetData, goalsData, transactions }) {
  const [phase,        setPhase]        = useState('pick');   // 'pick' | 'response'
  const [selectedMood, setSelectedMood] = useState(null);
  const [hoveredMood,  setHoveredMood]  = useState(null);

  const handlePickMood = (moodId) => {
    setSelectedMood(moodId);
    setPhase('response');
    onComplete(moodId);  // save immediately, don't block on response phase
  };

  const option   = MOOD_OPTIONS.find(m => m.id === selectedMood);
  const response = selectedMood
    ? buildResponse(selectedMood, budgetData, goalsData, transactions)
    : null;
  const colors   = response ? (COLOR_MAP[response.color] || COLOR_MAP.slate) : COLOR_MAP.slate;

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-1">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-blue-400" />
            <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
              Weekly Check-In
            </span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-300 hover:bg-slate-800 transition-colors"
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Phase 1: Pick mood ── */}
        {phase === 'pick' && (
          <div className="px-6 pb-6 pt-4">
            <h2 className="text-xl font-bold text-slate-100 mb-1">
              How are you feeling about your finances this week?
            </h2>
            <p className="text-slate-400 text-sm mb-6">
              No right or wrong answer — just a quick pulse check. 💙
            </p>

            <div className="grid grid-cols-5 gap-2 mb-5">
              {MOOD_OPTIONS.map(m => (
                <button
                  key={m.id}
                  onClick={() => handlePickMood(m.id)}
                  onMouseEnter={() => setHoveredMood(m.id)}
                  onMouseLeave={() => setHoveredMood(null)}
                  className={`
                    flex flex-col items-center gap-2 p-3 rounded-xl border transition-all duration-150
                    ${hoveredMood === m.id
                      ? `bg-gradient-to-b ${m.gradient} ${m.border} scale-105 shadow-lg`
                      : 'bg-slate-800/50 border-slate-700/60 hover:border-slate-600'}
                  `}
                >
                  <span className="text-2xl leading-none select-none">{m.emoji}</span>
                  <span className={`text-xs font-medium transition-colors ${hoveredMood === m.id ? 'text-slate-100' : 'text-slate-400'}`}>
                    {m.label}
                  </span>
                </button>
              ))}
            </div>

            <button
              onClick={onClose}
              className="w-full text-xs text-slate-600 hover:text-slate-400 transition-colors py-1"
            >
              Skip for now
            </button>
          </div>
        )}

        {/* ── Phase 2: Personalized response ── */}
        {phase === 'response' && response && option && (
          <div className="px-6 pb-6 pt-3">
            {/* Mood picked confirmation */}
            <div className="flex items-center gap-3 mb-5">
              <span className="text-3xl leading-none">{option.emoji}</span>
              <div>
                <p className="text-sm font-semibold text-slate-100">You said: {option.label}</p>
                <p className="text-xs text-slate-500">Check-in saved for this week.</p>
              </div>
            </div>

            {/* Response card */}
            <div className={`rounded-xl border p-4 space-y-3 ${colors.bg} ${colors.border}`}>
              <p className={`text-sm font-semibold leading-snug ${colors.heading}`}>
                {response.heading}
              </p>
              {response.body && (
                <p className={`text-xs ${colors.body}`}>{response.body}</p>
              )}
              <ul className="space-y-2">
                {response.points.map((pt, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${colors.bullet}`} />
                    <span className="text-xs text-slate-300 leading-relaxed">{pt}</span>
                  </li>
                ))}
              </ul>
            </div>

            <button
              onClick={onClose}
              className="mt-4 w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold py-2.5 rounded-xl transition-colors"
            >
              Let's go <ArrowRight size={15} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
